import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import RunningMessagesBanner, {
  MessageItem,
} from '@/components/RunningMessagesBanner';
import { HeaderWrapper } from '@/components/StyledComponents';
import {
  LandingConfig,
  secondaryUrlError,
} from '@/pages/admin/clientManagement/ClientOnBoarding/LoginPageCustomizer/landing-config';
import LandingPreview from '@/pages/admin/clientManagement/ClientOnBoarding/LoginPageCustomizer/LandingPreview';

/**
 * Ceiling on the hero. Without it, a tall screen stretches the image to its
 * full height and strands the copy in a sea of whitespace; past this the hero
 * keeps its shape and the spare height falls below it.
 */
const HERO_MAX_HEIGHT = 640;

interface LandingPageShellProps {
  c: LandingConfig;
  logo: string;
  messages: MessageItem[];
  onLoginClick?: () => void;
  loginLoading?: boolean;
}

/**
 * The whole landing page — header, running-messages banner, hero and footer —
 * sized to fill its parent exactly, with nothing scrolling. The live page gives
 * it a 100dvh parent; the admin preview renders it at a fixed size and scales
 * it down, so both show the same layout.
 *
 * Below `md` the hero stacks (image over copy) and is taller than a phone
 * screen, so there the page scrolls vertically and the hero height cap is off.
 */
const LandingPageShell = ({
  c,
  logo,
  messages,
  onLoginClick,
  loginLoading = false,
}: LandingPageShellProps) => {
  const theme = useTheme();

  // The secondary CTA opens the configured URL in a new tab. Re-checked here
  // (not just at authoring time) because tenant-info is external data — a
  // non-https value must never be handed to window.open.
  const secondaryUrl = c.secondaryUrl?.trim();
  const onSecondaryClick =
    secondaryUrl && !secondaryUrlError(true, secondaryUrl)
      ? () => window.open(secondaryUrl, '_blank', 'noopener,noreferrer')
      : undefined;

  return (
    <Box
      display='flex'
      flexDirection='column'
      height='100%'
      sx={{
        overflowX: 'hidden',
        overflowY: { xs: 'auto', md: 'hidden' },
        background: theme.palette.background.default,
      }}
    >
      <HeaderWrapper sx={{ flexShrink: 0, px: { xs: 2, sm: 2.5 } }}>
        <Avatar
          src={logo}
          variant='square'
          sx={{
            '& img': {
              objectFit: 'contain !important',
            },
            /* Sized to the image, not to a fixed box: the bundled fallback is a
               101x32 wordmark, but a tenant may upload a square mark. A fixed
               101 width would letterbox that square inside `contain` and strand
               the logo ~35px right of the gutter every other row lines up on. */
            width: 'auto',
            maxWidth: 101,
            height: 32,
            cursor: 'pointer',
          }}
        />
        {/* The hero's primary CTA is already a Login; don't offer two. */}
        {!c.primaryEnabled && (
          <Button
            data-testid='header-login'
            variant='contained'
            startIcon={loginLoading ? <CircularProgress size='1rem' /> : null}
            disabled={loginLoading}
            onClick={onLoginClick}
          >
            Login
          </Button>
        )}
      </HeaderWrapper>

      {/* Fills whatever the header and footer leave behind, so nothing scrolls. */}
      <Box
        display='flex'
        flexDirection='column'
        gap={1.5}
        py={1.5}
        sx={{
          flex: { xs: '1 0 auto', md: 1 },
          minHeight: { xs: 'auto', md: 0 },
          px: { xs: 2, sm: 2.5 },
          maxWidth: 1440,
          width: '100%',
          margin: '0 auto',
        }}
      >
        <Box flexShrink={0}>
          <RunningMessagesBanner messages={messages} />
        </Box>

        <Box
          display='flex'
          alignItems='flex-start'
          justifyContent='center'
          sx={{
            flex: { xs: '1 0 auto', md: 1 },
            minHeight: { xs: 'auto', md: 0 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: { xs: 'auto', md: '100%' },
              maxHeight: { xs: 'none', md: HERO_MAX_HEIGHT },
            }}
          >
            <LandingPreview
              c={c}
              onPrimaryClick={onLoginClick}
              onSecondaryClick={onSecondaryClick}
              primaryLoading={loginLoading}
            />
          </Box>
        </Box>
      </Box>

      <Box
        py={1.5}
        flexShrink={0}
        display='flex'
        alignItems='center'
        fontSize={12}
        sx={{
          px: { xs: 2, sm: 3.5 },
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 0.5,
          justifyContent: { xs: 'center', sm: 'space-between' },
          backgroundColor: theme.colors.primary.main,
          color: 'white',
        }}
      >
        <Typography>Powered by - Shares.Vision</Typography>
        <Typography>©Trusted services</Typography>
      </Box>
    </Box>
  );
};

export default LandingPageShell;
